import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Insights as AnalyticsIcon,
  People as PeopleIcon,
  Article as ArticleIcon,
  RecordVoiceOver as VoiceIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Payment as PaymentIcon,
  DeleteForever as DeleteForeverIcon,
  LocalOffer as PromoIcon,
  MonitorHeart as DiagnosticsIcon,
} from "@mui/icons-material";
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
} from "@mui/material";
import { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";

import analyticsCopy from "../../public/locales/en/analytics.json";
import diagnosticsCopy from "../../public/locales/en/diagnostics.json";
import textQualityCopy from "../../public/locales/en/textQuality.json";
import { useAppDispatch, useAppSelector } from "../store";
import { adminLogout } from "../store/slices/adminAuthSlice";

const drawerWidth = 240;

const menuItems = [
  { text: "Dashboard", icon: <DashboardIcon />, path: "/dashboard" },
  {
    text: analyticsCopy.navigation,
    icon: <AnalyticsIcon />,
    path: "/analytics",
  },
  { text: "Users", icon: <PeopleIcon />, path: "/users" },
  { text: "Texts", icon: <ArticleIcon />, path: "/texts" },
  {
    text: textQualityCopy.navigation,
    icon: <ArticleIcon />,
    path: "/text-quality",
  },
  { text: "Voices", icon: <VoiceIcon />, path: "/voices" },
  {
    text: "Subscription Plans",
    icon: <PaymentIcon />,
    path: "/subscription-plans",
  },
  {
    text: "Deletion Requests",
    icon: <DeleteForeverIcon />,
    path: "/deletions",
  },
  {
    text: "Promo Codes",
    icon: <PromoIcon />,
    path: "/promo-codes",
  },
  { text: "Settings", icon: <SettingsIcon />, path: "/settings" },
  {
    text: diagnosticsCopy.navigation,
    icon: <DiagnosticsIcon />,
    path: "/diagnostics",
  },
];

const AdminLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.adminAuth);

  const handleDrawerToggle = (): void => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = (): void => {
    dispatch(adminLogout());
    navigate("/login");
  };

  const drawer = (
    <Box>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          LingoShade Admin
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Admin Panel
          </Typography>
          <Typography
            variant="body2"
            noWrap
            sx={{ mr: 2, maxWidth: { xs: 90, sm: "none" } }}
          >
            {user?.email}
          </Typography>
          <Button
            color="inherit"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          p: { xs: 2, sm: 3 },
          width: { xs: "100%", sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminLayout;
